/**
 * RoamerIntelligenceEngine.ts — Engine #2: the Roamer Intelligence Engine™.
 *
 * The recommendation brain behind the Roamer Local card. Answers one question:
 * "What is best for THIS user in THIS city right now?"
 *
 * ARCHITECTURE:
 *   • Orchestration only — data comes from ProviderRegistry contracts
 *     (placeholder today, real APIs later, plug-and-play), scores come from
 *     the pure scoring modules. No hardcoded city logic or recommendations.
 *   • Context (base city, profile, time) is resolved per call: the base comes
 *     from the Roamer Location Engine™ (single source of truth) and the
 *     profile from RoamerProfileService — both overridable for tests.
 *   • Every recommendation returned is RANKED and carries an explainable
 *     Roamer Match Score™ (score + "Why?" reasons + concerns + confidence).
 *   • Results are memoised per (location, profile, category) signature and
 *     invalidated automatically when the base or the profile changes.
 */

import { roamerLocationEngine, locationSignature } from '@/services/RoamerLocationEngine';
import type { RoamerLocation } from '@/services/RoamerLocationEngine';
import { roamerProfileService, PROFILE_CHANGED_EVENT } from './RoamerProfileService';
import { profileSignature, type RoamerProfile } from './RoamerProfile';
import { providerRegistry, type ProviderRegistry } from './providers/ProviderRegistry';
import { localDayIndex } from './providers/mockData';
import type { CommunityReview, ProviderMeta } from './providers/types';
import { calculateMatchScore } from './scoring/matchScoring';
import { calculateRoamerScore } from './scoring/roamerScoring';
import { buildDailySuggestions } from './dailyIntelligence';
import { explainRecommendations } from './explainability/ExplainableRecommendationLayer';
import type {
  ExplainedRecommendation,
  RecommendationExplanation,
} from './explainability/types';
import type {
  DailySuggestion,
  IntelligenceContext,
  MatchScore,
  Recommendation,
  RecommendationCategory,
  RecommendationQueryOptions,
  RoamerScore,
  RoamerScoreCategory,
} from './types';

const DEFAULT_LIMIT = 6;
const LOCATION_CHANGED_EVENT = 'nomad:location-changed';

// ── Deep-clone helpers ────────────────────────────────────────────────────────
// Cached results must never share mutable references with what callers receive,
// or external mutation could corrupt the cache and break determinism.

function cloneMatch(m: MatchScore): MatchScore {
  return {
    ...m,
    reasons: m.reasons.map((r) => ({ ...r })),
    concerns: m.concerns.map((c) => ({ ...c })),
  };
}

function cloneExplanation(e: RecommendationExplanation): RecommendationExplanation {
  return {
    ...e,
    whyThis: [...e.whyThis],
    whyNot: [...e.whyNot],
    supportingMetrics: e.supportingMetrics.map((m) => ({ ...m })),
    alternatives: e.alternatives.map((a) => ({
      ...a,
      drawbacks: [...a.drawbacks],
      advantages: [...a.advantages],
    })),
  };
}

function cloneExplained(r: ExplainedRecommendation): ExplainedRecommendation {
  return {
    ...r,
    tags: [...r.tags],
    attributes: {
      ...r.attributes,
      amenities: r.attributes.amenities ? [...r.attributes.amenities] : undefined,
    },
    location: { ...r.location },
    match: r.match ? cloneMatch(r.match) : undefined,
    explanation: cloneExplanation(r.explanation),
  };
}

export class RoamerIntelligenceEngine {
  private cache = new Map<string, unknown>();

  constructor(private registry: ProviderRegistry = providerRegistry) {
    // Invalidate memoised results whenever the base or profile changes.
    if (typeof window !== 'undefined') {
      window.addEventListener(LOCATION_CHANGED_EVENT, () => this.cache.clear());
      window.addEventListener(PROFILE_CHANGED_EVENT, () => this.cache.clear());
    }
  }

  // ── Context resolution ───────────────────────────────────────────────────────

  private resolveContext(opts?: RecommendationQueryOptions): IntelligenceContext {
    return {
      location: opts?.location ?? roamerLocationEngine.getCurrentLocation(),
      profile: opts?.profile ?? roamerProfileService.getProfile(),
      now: opts?.now ?? Date.now(),
    };
  }

  private cacheKey(kind: string, ctx: IntelligenceContext, extra = ''): string {
    return `${kind}|${locationSignature(ctx.location)}|${profileSignature(ctx.profile)}|${extra}`;
  }

  // ── Core ranking pipeline ────────────────────────────────────────────────────

  private async recommend(
    category: RecommendationCategory,
    opts?: RecommendationQueryOptions,
  ): Promise<ExplainedRecommendation[]> {
    const ctx = this.resolveContext(opts);
    const limit = opts?.limit ?? DEFAULT_LIMIT;
    const key = this.cacheKey(`rec:${category}`, ctx, String(limit));
    const cached = this.cache.get(key) as ExplainedRecommendation[] | undefined;
    if (cached) return cached.map(cloneExplained);

    const raw =
      category === 'housing'
        ? await this.registry.get('housing').getListings({
            location: ctx.location,
            limit,
            maxMonthlyUsd: ctx.profile.housingBudgetUsd,
            minStayDays: ctx.profile.lengthOfStayDays,
          })
        : await this.registry.get('places').getPlaces({ location: ctx.location, category, limit });

    // Score, then rank: match score first, community rating as the tie-breaker.
    const ranked = raw
      .map((rec) => ({ ...rec, match: calculateMatchScore(rec, ctx.profile) }))
      .sort(
        (a, b) =>
          b.match.score - a.match.score ||
          (b.attributes.rating ?? 0) - (a.attributes.rating ?? 0),
      );

    // REQUIRED stage: every recommendation passes through the Explainable
    // Recommendation Layer before it can reach the UI. Nothing bypasses it.
    const explained = explainRecommendations(ranked);

    this.cache.set(key, explained);
    return explained.map(cloneExplained);
  }

  // ── Public recommendation API (modular, one per domain) ─────────────────────

  getHousingRecommendations(opts?: RecommendationQueryOptions)        { return this.recommend('housing', opts); }
  getCoworkingRecommendations(opts?: RecommendationQueryOptions)      { return this.recommend('coworking', opts); }
  getCafeRecommendations(opts?: RecommendationQueryOptions)           { return this.recommend('cafe', opts); }
  getRestaurantRecommendations(opts?: RecommendationQueryOptions)     { return this.recommend('restaurant', opts); }
  getNeighborhoodRecommendations(opts?: RecommendationQueryOptions)   { return this.recommend('neighborhood', opts); }
  getTransportationRecommendations(opts?: RecommendationQueryOptions) { return this.recommend('transportation', opts); }
  getUtilityRecommendations(opts?: RecommendationQueryOptions)        { return this.recommend('utility', opts); }
  getEmergencyResources(opts?: RecommendationQueryOptions)            { return this.recommend('emergency', opts); }
  getCommunityRecommendations(opts?: RecommendationQueryOptions)      { return this.recommend('community', opts); }

  /** Ranked recommendations for any category — the generic entry point. */
  getRecommendations(category: RecommendationCategory, opts?: RecommendationQueryOptions) {
    return this.recommend(category, opts);
  }

  // ── Scores ───────────────────────────────────────────────────────────────────

  /** City-level Roamer Score™ for the current (or given) base. */
  async calculateRoamerScore(
    opts?: RecommendationQueryOptions & {
      weightOverrides?: Partial<Record<RoamerScoreCategory, number>>;
    },
  ): Promise<RoamerScore> {
    const ctx = this.resolveContext(opts);
    const key = this.cacheKey('roamer-score', ctx, JSON.stringify(opts?.weightOverrides ?? {}));
    const cached = this.cache.get(key) as RoamerScore | undefined;
    if (cached) return { ...cached, breakdown: cached.breakdown.map((b) => ({ ...b })) };

    const metrics = await this.registry.get('cityMetrics').getMetrics(ctx.location);
    const score = calculateRoamerScore(metrics, ctx.profile, opts?.weightOverrides);
    this.cache.set(key, score);
    return { ...score, breakdown: score.breakdown.map((b) => ({ ...b })) };
  }

  /** Roamer Match Score™ for a single recommendation (pure, synchronous). */
  calculateMatchScore(rec: Recommendation, profile?: RoamerProfile): MatchScore {
    return calculateMatchScore(rec, profile ?? roamerProfileService.getProfile());
  }

  // ── Daily intelligence ───────────────────────────────────────────────────────

  async getDailySuggestions(opts?: RecommendationQueryOptions): Promise<DailySuggestion[]> {
    const ctx = this.resolveContext(opts);
    const key = this.cacheKey('daily', ctx, String(localDayIndex(ctx.location, ctx.now)));
    const cached = this.cache.get(key) as DailySuggestion[] | undefined;
    if (cached) return cached.map((s) => ({ ...s }));

    const suggestions = await buildDailySuggestions(ctx, this.registry);
    this.cache.set(key, suggestions);
    return suggestions.map((s) => ({ ...s }));
  }

  // ── Supporting surfaces ──────────────────────────────────────────────────────

  /** Community reviews for a specific recommendation (provider pass-through). */
  getReviews(placeId: string, limit = 5): Promise<CommunityReview[]> {
    return this.registry.get('communityReviews').getReviews(placeId, limit);
  }

  /** Which providers back the engine right now (live vs placeholder). */
  getProviderStatus(): ProviderMeta[] {
    return this.registry.listProviders();
  }

  /**
   * Drop memoised results. With a location, SMART invalidation: only entries
   * for that base are dropped (cache keys embed the location signature) —
   * other bases' data stays warm. Without arguments, full clear (providers
   * changed, manual refresh, tests).
   */
  clearCache(location?: RoamerLocation): void {
    if (!location) {
      this.cache.clear();
      return;
    }
    const sig = locationSignature(location);
    for (const key of this.cache.keys()) {
      if (key.includes(`|${sig}|`)) this.cache.delete(key);
    }
  }
}

/** App-wide singleton — the Local card and future surfaces import this. */
export const roamerIntelligenceEngine = new RoamerIntelligenceEngine();
